import { assign, fromPromise, setup } from "xstate";

export interface DiscoveryFailure {
  code: "aborted" | "network" | "unavailable";
  message: string;
  retryable: boolean;
}

export interface DiscoveryResourceContext<Query, Item> {
  query: Query;
  queryKey: string;
  items: readonly Item[];
  failure: DiscoveryFailure | null;
  revision: number;
}

export interface DiscoveryResourceInput<Query> {
  query: Query;
}

export type DiscoveryResourceEvent<Query> =
  | { type: "QUERY.CHANGED"; query: Query }
  | { type: "RETRY" }
  | { type: "CANCEL" };

export interface DiscoveryResourceOptions<Query, Item> {
  key(query: Query): string;
  load(input: {
    query: Query;
    queryKey: string;
    signal: AbortSignal;
  }): Promise<readonly Item[]>;
  onResolved?(input: {
    query: Query;
    queryKey: string;
    items: readonly Item[];
  }): void;
  onRejected?(input: {
    query: Query;
    queryKey: string;
    failure: DiscoveryFailure;
  }): void;
}

function discoveryFailure(error: unknown): DiscoveryFailure {
  if (error instanceof DOMException && error.name === "AbortError") {
    return {
      code: "aborted",
      message: "Discovery was cancelled",
      retryable: true,
    };
  }
  return {
    code: "network",
    message: error instanceof Error ? error.message : "Discovery failed",
    retryable: true,
  };
}

/**
 * Creates a domain-specific, keyed discovery actor. Exiting `loading` aborts
 * the request; XState also discards completion events from the stopped invoke,
 * so an old filter/query cannot overwrite a newer result.
 */
export function createDiscoveryResourceMachine<Query, Item>(
  options: DiscoveryResourceOptions<Query, Item>,
) {
  const loadResource = fromPromise<
    readonly Item[],
    { query: Query; queryKey: string }
  >(async ({ input, signal }) =>
    options.load({
      query: input.query,
      queryKey: input.queryKey,
      signal,
    }),
  );

  return setup({
    types: {
      context: {} as DiscoveryResourceContext<Query, Item>,
      events: {} as DiscoveryResourceEvent<Query>,
      input: {} as DiscoveryResourceInput<Query>,
    },
    actors: { loadResource },
    guards: {
      hasItems: (_, params: { items: readonly Item[] }) =>
        params.items.length > 0,
    },
    actions: {
      changeQuery: assign(
        ({ context }, params: { query: Query; queryKey: string }) => ({
          ...context,
          query: params.query,
          queryKey: params.queryKey,
          items: [],
          failure: null,
          revision: context.revision + 1,
        }),
      ),
      rememberItems: assign({
        items: (_, params: { items: readonly Item[] }) => params.items,
        failure: null,
      }),
      rememberFailure: assign({
        items: [],
        failure: (_, params: { error: unknown }) =>
          discoveryFailure(params.error),
      }),
      publishItems: (
        _,
        params: {
          query: Query;
          queryKey: string;
          items: readonly Item[];
        },
      ) => options.onResolved?.(params),
      publishFailure: (
        _,
        params: {
          query: Query;
          queryKey: string;
          error: unknown;
        },
      ) =>
        options.onRejected?.({
          query: params.query,
          queryKey: params.queryKey,
          failure: discoveryFailure(params.error),
        }),
      clearFailure: assign({ failure: null }),
    },
  }).createMachine({
    id: "discoveryResource",
    context: ({ input }) => ({
      query: input.query,
      queryKey: options.key(input.query),
      items: [],
      failure: null,
      revision: 0,
    }),
    initial: "loading",
    states: {
      idle: {
        on: { RETRY: { target: "loading" } },
      },
      loading: {
        invoke: {
          src: "loadResource",
          input: ({ context }) => ({
            query: context.query,
            queryKey: context.queryKey,
          }),
          onDone: [
            {
              guard: {
                type: "hasItems",
                params: ({ event }) => ({ items: event.output }),
              },
              target: "ready",
              actions: [
                {
                  type: "rememberItems",
                  params: ({ event }) => ({ items: event.output }),
                },
                {
                  type: "publishItems",
                  params: ({ context, event }) => ({
                    query: context.query,
                    queryKey: context.queryKey,
                    items: event.output,
                  }),
                },
              ],
            },
            {
              target: "empty",
              actions: [
                {
                  type: "rememberItems",
                  params: ({ event }) => ({ items: event.output }),
                },
                {
                  type: "publishItems",
                  params: ({ context, event }) => ({
                    query: context.query,
                    queryKey: context.queryKey,
                    items: event.output,
                  }),
                },
              ],
            },
          ],
          onError: {
            target: "failed",
            actions: [
              {
                type: "rememberFailure",
                params: ({ event }) => ({ error: event.error }),
              },
              {
                type: "publishFailure",
                params: ({ context, event }) => ({
                  query: context.query,
                  queryKey: context.queryKey,
                  error: event.error,
                }),
              },
            ],
          },
        },
      },
      ready: {},
      empty: {},
      failed: {
        on: {
          RETRY: {
            target: "loading",
            actions: "clearFailure",
          },
        },
      },
    },
    on: {
      "QUERY.CHANGED": {
        target: ".loading",
        reenter: true,
        actions: {
          type: "changeQuery",
          params: ({ event }) => ({
            query: event.query,
            queryKey: options.key(event.query),
          }),
        },
      },
      CANCEL: {
        target: ".idle",
      },
    },
  });
}
